import React from "react";
import { FileText } from "lucide-react";

export function ExecutiveSummaryCard({ executiveSummary, workflow, contract }) {
  const isVisible = executiveSummary && (
    workflow?.stage === "approval" || workflow?.stage === "completed" ||
    contract?.status === "approved" || contract?.status === "pending_approval"
  );

  if (!isVisible) return null;

  return (
    <div className="mt-6 border-t pt-6" data-testid="executive-summary">
      <h3 className="text-sm font-mono uppercase tracking-wider text-slate-500 mb-4 flex items-center gap-2">
        <FileText size={16} /> Executive Summary for Approver
      </h3>

      {/* Overview */}
      <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-sm border mb-4">
        <p className="text-sm text-slate-700 dark:text-slate-300">{executiveSummary.overview}</p>
      </div>

      {/* Recommendation Badge */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs uppercase tracking-wider text-slate-500">AI Recommendation:</span>
        <span className={`text-xs font-bold px-3 py-1 rounded-sm ${
          executiveSummary.approval_recommendation === "approve_with_confidence" ? "bg-emerald-100 text-emerald-700" :
          executiveSummary.approval_recommendation === "approve_with_caution" ? "bg-amber-100 text-amber-700" :
          "bg-red-100 text-red-700"
        }`} data-testid="approval-recommendation">
          {executiveSummary.approval_recommendation?.replace(/_/g, " ").toUpperCase()}
        </span>
      </div>
      {executiveSummary.recommendation_rationale && (
        <p className="text-xs text-slate-500 italic mb-4">{executiveSummary.recommendation_rationale}</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {executiveSummary.key_commercial_terms?.length > 0 && (
          <div className="p-3 border rounded-sm">
            <p className="text-xs font-mono uppercase text-slate-500 mb-2">Key Commercial Terms</p>
            <ul className="space-y-1">
              {executiveSummary.key_commercial_terms.map((t, i) => (
                <li key={i} className="text-sm text-slate-700 flex items-start gap-1.5">
                  <span className="text-slate-400 shrink-0">-</span> {t}
                </li>
              ))}
            </ul>
          </div>
        )}
        {executiveSummary.missing_protections?.length > 0 && (
          <div className="p-3 border border-amber-200 bg-amber-50/50 rounded-sm">
            <p className="text-xs font-mono uppercase text-amber-600 mb-2">Missing Protections</p>
            <ul className="space-y-1">
              {executiveSummary.missing_protections.map((p, i) => (
                <li key={i} className="text-sm text-amber-800 flex items-start gap-1.5">
                  <span className="shrink-0">-</span> {p}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {executiveSummary.high_risk_clauses?.length > 0 && (
        <div className="mt-4 p-3 border border-red-200 bg-red-50/50 rounded-sm">
          <p className="text-xs font-mono uppercase text-red-600 mb-2">High Risk Clauses</p>
          <div className="space-y-3">
            {executiveSummary.high_risk_clauses.map((c, i) => (
              <div key={i} className="text-sm">
                <p className="font-medium text-red-800">{c.clause}</p>
                <p className="text-red-700 text-xs mt-0.5">Risk: {c.risk}</p>
                {c.recommendation && <p className="text-slate-600 text-xs mt-0.5 italic">Recommendation: {c.recommendation}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {executiveSummary.non_standard_clauses?.length > 0 && (
        <div className="mt-4 p-3 border border-indigo-200 bg-indigo-50/50 rounded-sm">
          <p className="text-xs font-mono uppercase text-indigo-600 mb-2">Non-Standard Clauses</p>
          <div className="space-y-2">
            {executiveSummary.non_standard_clauses.map((c, i) => (
              <div key={i} className="text-sm">
                <p className="font-medium text-indigo-800">{c.clause}</p>
                <p className="text-slate-600 text-xs mt-0.5">{c.note}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[10px] text-slate-400 italic mt-3">
        This AI-generated summary is for informational purposes only and does not constitute legal advice. Always review the full contract before approving.
      </p>
    </div>
  );
}
